import { useEffect, useRef } from 'react';
import type { JSX, ReactNode } from 'react';

import classNames from 'classnames';

import CloseIcon from '@admin-platform/ui/icons/outline/x-mark.icon.svg?react';

import styles from './Toast.module.css';

export const ToastType = {
  success: 'success',
  error: 'error',
  warning: 'warning',
} as const;

interface ToastProps {
  onDismiss(): void;
  type: keyof typeof ToastType;
  message: ReactNode;
}

const DISMISS_AFTER_MS = 5000;

export default function Toast({
  type,
  message,
  onDismiss,
}: ToastProps): JSX.Element {
  // Keep latest onDismiss in a ref so the timer is set only once on mount.
  // Without this, every parent re-render produces a fresh onDismiss closure,
  // re-running the effect, clearing the timer, and starting a new one — which
  // means rapid toast bursts never auto-dismiss.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    const dismissTimerId = setTimeout(
      () => onDismissRef.current(),
      DISMISS_AFTER_MS,
    );

    return () => clearTimeout(dismissTimerId);
  }, []);

  return (
    <div className={classNames(styles.Toast, styles[type])}>
      <div>{message}</div>
      <button type="button" onClick={onDismiss}>
        <CloseIcon className={styles.CloseIcon} />
      </button>
    </div>
  );
}
